/**
 * Authentication Context
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { authApiService } from '../services/auth-api';
import { logger } from '@metlas/shared';
import type { User, AuthContextState } from '../types/auth';

const AuthContext = createContext<AuthContextState | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const currentUser = await authApiService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      logger.error('[AuthContext] Failed to check auth:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (username: string, password: string) => {
    const response = await authApiService.login(username, password);
    if (response.data?.user) {
      setUser(response.data.user);
    } else {
      throw new Error('Invalid login response');
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApiService.logout();
      setUser(null);
    } catch (error) {
      logger.error('[AuthContext] Logout failed:', error);
      setUser(null);
      throw error;
    }
  }, []);

  const value: AuthContextState = { user, loading, login, logout, checkAuth };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextState => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
